import type { SnakeToCamelCase } from 'humps'

export enum SilverfinContext {
  Account = 'account',
  BudgetSet = 'budget_set',
  Company = 'company',
  CompanyWorkflow = 'company_workflow',
  Document = 'document',
  ReconciliationText = 'reconciliation_text',
  ReportTemplate = 'report_template',
  None = 'none',
}

export enum SilverfinContextCamelCase {
  Account = 'Account',
  BudgetSet = 'BudgetSet',
  Company = 'Company',
  CompanyWorkflow = 'CompanyWorkflow',
  Document = 'Document',
  ReconciliationText = 'ReconciliationText',
  ReportTemplate = 'ReportTemplate',
  None = 'None',
}

type NameFieldsOfLanguage<L> = {
  [K in keyof L as `originalName${Capitalize<string & K>}`]?: string
} &
  {
    [K in keyof L as `customName${Capitalize<string & K>}`]?: string
  }

type NameFields = NameFieldsOfLanguage<{
  nl: string
  fr: string
  en: string
  de: string
  es: string
  fi: string
  it: string
  nb: string
  pl: string
  se: string
  cs: string
  ar: string
  az: string
  bs: string
  bg: string
  el: string
  et: string
  hr: string
  hu: string
  id: string
  is: string
  ja: string
  ka: string
  kk: string
  km: string
  ko: string
  lt: string
  lv: string
  pt: string
  ro: string
  ru: string
  sk: string
  sl: string
  sr: string
  th: string
  tr: string
  uk: string
  vi: string
  zh: string
  da: string
  ca: string
}>

export type Account = {
  id: number
  number: string
  createdAt: string
  updatedAt: string
  companyId: number
  description: string | null
  originalNumber: string
  accountDetailTemplateId: number | null
  mapped: boolean
  remarksUpdatedAt: string | null
  accountType: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  accountTypeExplicitlySet: boolean
  originalNameNl: string | null
  renamed: false
  externallyMappedNumber: string | null
  name: string
} & NameFields

export type Audit = {
  /** The action performed in the audit */
  action: AuditAction

  /**
   * The ID of the audit. Serialized as a string from the API for consistency
   * with the string IDs of virtual audits, e.g., audit groups
   */
  id: number

  auditableType: AuditableType

  /**
   * Used to determine the group of audits (e.g., consecutive audits with the
   * same grouping key will be displayed under an Audit Group)
   */
  groupingKey: string | null

  hasDetails: boolean

  /**
   * Used by Vue `<component :is="...">` to render the appropriate component
   * in the AuditLog, e.g., 'audit-log-item' (maps to a Vue component name)
   */
  kind: 'audit-log-item' | 'audit-log-group'

  message: string
  sortTimestamp: number
  trimmed: boolean
  username: string
}

export enum AuditAction {
  Create = 'create',
  Update = 'update',
  BulkStarUnstar = 'bulk_star_unstar',
  CopiedAdjustments = 'copied_adjustments',
  TemplateCopyData = 'template_copy_data',
  CheckDone = 'check_done',
  CheckUndone = 'check_undone',
  Star = 'star',
  Unstar = 'unstar',
  TagAdded = 'tag_added',
  TagRemoved = 'tag_removed',
  ReplaceFile = 'replace_file',
  RemoveFromPeriod = 'remove_from_period',
  Lock = 'lock',
  LockPermanently = 'lock_permanently',
  Unlock = 'unlock',
  Archive = 'archive',
  ArchiveTodo = 'archive_todo',
  Restore = 'restore',
  LockedLedgersUpToDate = 'locked_ledgers_up_to_date',
  UnlockedAccountNumbers = 'unlocked_account_numbers',
  ReviewOutdate = 'outdate',
}

export enum AuditableType {
  Account = 'Account',
  AccountEntry = 'AccountEntry',
  Adjustment = 'Adjustment',
  CheckLink = 'CheckLink',
  Comment = 'Comment',
  Company = 'Company',
  CompanySupportAccess = 'CompanySupportAccess',
  CompanyTemplate = 'CompanyTemplate',
  CompanyTemplateFirmWorkflow = 'CompanyTemplateFirmWorkflow',
  CompanyTemplatePermanentEntryLink = 'CompanyTemplatePermanentEntryLink',
  CompanyTemplateReportTemplateLink = 'CompanyTemplateReportTemplateLink',
  CompanyWorkflow = 'CompanyWorkflow',
  CompanyWorkflowStatus = 'CompanyWorkflowStatus',
  Detail = 'Detail',
  Document = 'Document',
  DocumentLink = 'DocumentLink',
  Export = 'Export',
  ExportFileInstance = 'ExportFileInstance',
  FirmWorkflow = 'FirmWorkflow',
  FirmWorkflowCheckList = 'FirmWorkflowCheckList',
  FirmWorkflowReconciliationText = 'FirmWorkflowReconciliationText',
  Ledger = 'Ledger',
  LedgerLockedAccountMapping = 'LedgerLockedAccountMapping',
  PermanentEntry = 'PermanentEntry',
  ReconciliationReview = 'ReconciliationReview',
  ReconciliationText = 'ReconciliationText',
  Remark = 'Remark',
  Role = 'Role',
  TextProperty = 'TextProperty',
  Todo = 'Todo',
  User = 'User',
  LedgerCheckList = 'LedgerCheckList',
  ReconciliationProposal = 'ReconciliationProposal',
  FirmEmailCustomization = 'FirmEmailCustomization',
  Trial = 'Trial',
  Firm = 'Firm',
}

export type SubAdjustment = {
  id: number
  adjustmentId: number
  value: string
  description: string
  createdAt: string
  updatedAt: string
  accountEntryId: number
  valueCredit: string | null
  valueDebit: string | null
  account: Account
  analyticalDimension0Id?: number | null
  analyticalDimension1Id?: number | null
  analyticalDimension2Id?: number | null
  analyticalDimension3Id?: number | null
  analyticalDimension4Id?: number | null
  analyticalDimension5Id?: number | null
  analyticalDimension6Id?: number | null
}

type BaseAdjustment = {
  id: number
  ledgerId: number
  ulid: number
  category: 'internal' | 'external'
  originalAdjustmentId: number | null
  originalAdjustmentType: 'forward' | 'reverse' | null
  forwardAccountId: number | null
  createdAt: string
  updatedAt: string
  postedAt: string
  reconciliationTextId: number | null
  permanentEntryId: number | null
  purpose: string | null
  ledger: {
    periodName: string
  }
}

export type Adjustment = Omit<BaseAdjustment, 'ledger'> & {
  checked: boolean
  categoryIds: Array<number>
  copyTypes: Array<'forward' | 'reverse' | null>
  createdAdjustments: Array<
    Pick<BaseAdjustment, 'id' | 'ulid' | 'originalAdjustmentType' | 'ledger'>
  >
  subAdjustments: Array<SubAdjustment>
  tagIds: Array<number>
  originalAdjustment: BaseAdjustment & {
    ledger: {
      periodName: string
    }
  }
}

export type AIOCRProposedDocuments = Record<
  string,
  {
    section: string
    documents: Array<{
      documentId: number
      documentName: string
    }>
  }
>

export enum DocumentLinkType {
  DirectUpload = 'DirectUpload',
  Document = 'Document',
  Export = 'Export',
  ExternalLink = 'ExternalLink',
  PermanentDocument = 'PermanentDocument',
}

export type LiquidBaseDocument = {
  document_id: number
  name: string
  size?: number
  previewable?: boolean
}

export type LiquidPeriodDocument = LiquidBaseDocument & {
  linkType: DocumentLinkType.Document
}

export type LiquidPermanentDocument = LiquidBaseDocument & {
  linkType: DocumentLinkType.PermanentDocument
}

export type LiquidExportDocument = LiquidBaseDocument & {
  linkType: DocumentLinkType.Export
}

export type LiquidExternalLinkDocument = Pick<
  LiquidBaseDocument,
  'document_id' | 'name'
> & {
  url: string
  linkType: DocumentLinkType.ExternalLink
}

export type LiquidDocument =
  | LiquidPeriodDocument
  | LiquidPermanentDocument
  | LiquidExportDocument
  | LiquidExternalLinkDocument

export type DirectUploadDocument = {
  linkType: DocumentLinkType.DirectUpload
  link: string
  name: string
  contentType: string
  size: number
  unique_id: string
}

export type DocumentUploaderItem =
  | DirectUploadDocument
  | ExistingDocument
  | ExistingPermanentDocument
  | ExistingExport

export type ExistingPermanentDocument = {
  document_id: number
  linkType: DocumentLinkType.PermanentDocument
  name: string
  permanent_document_id: number
  unique_id: string
}

export type RawExistingPermanentDocument = {
  id: number
  title: string
  category: boolean
  icon: string
  documentId: number
  invisible: boolean
  documentUrl: string
  slugPath: string
}

export type ExistingDocument = {
  document_id: number
  linkType: DocumentLinkType.Document
  name: string
  unique_id: string
  uploadedBy?: string
  size?: number
}

export type RawExistingDocument = {
  id: number
  title: string
  icon: string
  category: boolean
  document: boolean
  attachments: string[]
}

export type ExistingExport = {
  linkType: DocumentLinkType.Export
  export_id: number
  name: string
  unique_id: string
  document_id: number
}

export type RawExistingExport = {
  id: number
  title: string
  export: true
  url: string
  exportedBy: string
  ledger: string
  ledgerId: number
  createdAt: string
  key: string
  status: 'succeeded' | 'failed' | 'pending'
  errorMessages: string[]
  links: Record<string, string>
}

export type RemarkTag = {
  archived: boolean
  id: number
  name: string
  talkingPoint: boolean
}

export type WorkflowReviewStatusData = {
  itemClass: 'reconciliation_text' | 'account_entry'
  itemId: number
  ledgerId: number
  review: { id: number; outdatedAt: number | null; userId?: number } | null
  reviews: Array<{ name: string; outdatedAt: number | null; time: number }>
  preparation: { id: number; time: string; name: string; userId: number } | null
}

export type ReviewItem = {
  id: number
  itemClass: 'reconciliation_text' | 'account_entry'
  itemName: string
  class: string
  compareBodyElement?: string
  contextType: 'reconciliation_text' | 'account'
  contextId: number
  contextHrefParam: string | null
  diffLimitReached?: boolean
  error?: string
  placeholder: boolean
  focused?: boolean
  bodyElement: string
  hasBodyElement?: boolean
  hasCompareBodyElement?: boolean
  headerElement: string
  messageCountElement: string
  reconciliationStatusIcon?: string
  activeAIProposal?: boolean
  workflowReviewStatusData?: WorkflowReviewStatusData
  solidTemplate?: SolidTemplateBoot
}

// Boot payload for a Solid template's client-side mount, embedded per review
// item by WorkflowReviewItemPresenter.
export type SolidTemplateBoot = {
  entryUrl: string
  stylesheetUrls: string[]
  requestContext: Record<string, unknown>
}

export type GenericParameters = Record<string, unknown>

export type CopyPreviousLedgerDataParameters = {
  actionPath: string
  confirmActionPath: string
  currentPeriodName: string
}

export type FillOpenTransactionsDataParameters = {
  actionPath: string
}

export type ChangeAccountDetailTemplateParameters = {
  actionPath: string
  accountEntryId: number
  currentTemplateName: string
  suggestedTemplateId: number
  suggestedTemplateName: string
  partnerTemplateDescription: string | null
}

export type CheckActionParameters =
  | CopyPreviousLedgerDataParameters
  | ChangeAccountDetailTemplateParameters
  | GenericParameters

export type CheckAction = {
  title: string
  actionType: string
  parameters: CheckActionParameters
}

export type Detail = {
  title: string
  description: string
}

type CheckText = {
  action: CheckAction
  description: string
  detail: {
    details: Detail[]
    tags: string
  }
}

type Document = {
  id: string
  filename: string
  url: string
}

export type Check = {
  accountId?: number
  reconciliationId?: number
  attachments?: Document[]
  actionType?: string
  id: string
  text: Record<string, CheckText>
  contextUrl?: string
  contextUrlText?: string
  readByUsers?: string[]
  followerIds?: number[]
  commentIds?: string[]
  documents?: Document[]
}

export type CheckContext = {
  id: number
  name: string
  type:
    | SilverfinContextCamelCase.Account
    | SilverfinContextCamelCase.ReconciliationText
  caption: string
  ledgerId: number
  url: string
}

export type CheckPayload = {
  accounts: Record<number, Check[]>
  reconciliations: Record<number, Check[]>
  general: Check[]
  contexts: CheckContext[]
  timestamp?: Date
  stale: boolean
  channel: string
  last_id: number
  readStatus: string[]
  readByUsers: Record<string, string[]>
  applyMentionsFilter?: boolean
}

export type ChecksCountPayload = {
  count: number
}

export type WorkflowFilterStatus =
  | 'show-all'
  | 'todos'
  | 'checks'
  | 'unreconciled'
  | 'not_prepared'
  | 'unreviewed_by_me'
  | 'prepared_unreviewed_by_me'
  | 'starred'

export type WorkflowFilterStatusCamelCase =
  | SnakeToCamelCase<WorkflowFilterStatus>
  | 'total'

export type WorkflowFilterStatusWithoutShowAll =
  | Exclude<WorkflowFilterStatusCamelCase, 'show-all'>
  | 'total'

export type WorkflowListItem = {
  id: number
  default: boolean
  name: string
  reconciliationTextIds: number[]
  accountIds: number[]
}
